// === Next.js ===
import type { NextRequest } from 'next/server';
import { NextResponse } from 'next/server';

// === Types & Interfaces ===
import type { AuthEventProps } from '@/lib/features/tracking/types';

// === Utils & Helpers ===
import { PortalRoutes, StudioRoutes } from '@/app/routes';
import { getPostAuthLandingUrl } from '@/@studio/utils';
import { EVENTS } from '@/lib/features/tracking/events';
import { tracking } from '@/lib/features/tracking/tracking';
import { addSecurityHeaders, matchesPath } from '@/types/utils';
import { getServerSession } from './config/auth';
import { AUTH_CONFIG } from './config/config';
import { handleUnauthorizedRedirect } from './types/guards';
import { createSignInUrl, validateCsrf } from './utils';

/**
 * Next-Auth API routes that should bypass middleware
 * @constant
 */
const NEXT_AUTH_ROUTES = [
    '/api/auth/callback',
    '/api/auth/signin',
    '/api/auth/signout',
    '/api/auth/session',
    '/api/auth/csrf',
    '/api/auth/providers',
    '/api/auth/error',
] as const;

/**
 * Authentication and security middleware for Next.js application
 * Handles session validation, CSRF protection, and security headers
 *
 * Flow:
 * 1. Skip middleware for Next-Auth routes
 * 2. Validate user session
 * 3. Handle portal-specific routes
 * 4. Handle protected routes
 * 5. Handle public routes
 * 6. Apply CSRF protection
 * 7. Add security headers
 *
 * @param request - Incoming Next.js request
 * @returns NextResponse with appropriate redirects or modifications
 *
 * @throws Will redirect to sign-in page for unauthorized access
 * @throws Will return 403 for CSRF validation failures
 * @throws Will return 500 for unhandled errors
 */
export async function middleware(request: NextRequest): Promise<NextResponse> {
    try {
        // === Initialize Response ===
        const response = NextResponse.next();
        const pathname = request.nextUrl.pathname;

        // === Next-Auth Route Handling ===
        // Skip middleware for Next-Auth API routes to prevent circular auth checks
        if (matchesPath(pathname, NEXT_AUTH_ROUTES)) {
            return response;
        }

        // Playwright E2E bypass — never active in production (guarded by NODE_ENV check).
        // Uses a request header because Edge Runtime middleware cannot read shell-level env vars.
        if (process.env.NODE_ENV !== 'production' && request.headers.get('x-e2e-test') === '1') {
            addSecurityHeaders(response);
            return response;
        }

        // === Session Validation ===
        // Get and validate the user session
        const session = await getServerSession();
        // Must also check `session.error` (e.g. RefreshAccessTokenError/TokenExpiredError),
        // not just that `session.user` is populated — otherwise a session that's expired but
        // still carries a stale user object reads as "authenticated" here while the client's
        // own `useSessionError` (src/@auth/hooks/useAuth.ts) correctly treats it as invalid.
        // That disagreement was a real, confirmed cause of an infinite redirect loop: this
        // middleware kept bouncing the user away from /sign-in (believing them authenticated)
        // while the client kept bouncing them back to /sign-in (believing the session dead).
        const isAuthenticated = !!session?.user && !session.error;

        // === Portal Route Protection ===
        if (pathname.startsWith(PortalRoutes.PORTAL)) {
            // Require authentication for all portal routes
            if (!isAuthenticated) {
                tracking.event<AuthEventProps>(EVENTS.USER.SESSION_ERROR, {
                    errorType: 'InvalidSession',
                    source: 'middleware.portal',
                });
                return NextResponse.redirect(createSignInUrl({ request, pathname }));
            }

            // Redirect portal root to default page
            if (pathname === PortalRoutes.PORTAL) {
                return NextResponse.redirect(new URL(StudioRoutes.MY_SURVEYS, request.url));
            }
        }

        // === Protected Route Handling ===
        // Check authentication for configured protected routes
        else if (matchesPath(pathname, AUTH_CONFIG.routes.protected)) {
            if (!isAuthenticated) {
                return NextResponse.redirect(
                    createSignInUrl({ request, pathname, error: 'SessionExpired' }),
                );
            }
        }

        // Redirect authenticated users away from public pages (e.g., sign-in).
        if (matchesPath(pathname, AUTH_CONFIG.routes.public) && isAuthenticated) {
            return NextResponse.redirect(new URL(getPostAuthLandingUrl(), request.url));
        }

        // === CSRF Protection ===
        // Validate CSRF tokens for protected endpoints
        if (matchesPath(pathname, AUTH_CONFIG.routes.csrfProtected)) {
            if (!validateCsrf(request)) {
                tracking.event<AuthEventProps>(EVENTS.AUTH.API_ERROR, {
                    errorType: 'CSRFValidationFailed',
                    source: 'middleware.csrf',
                    path: pathname,
                });
                return new NextResponse(null, { status: 403 });
            }
        }

        // === Security Headers ===
        // Add standard security headers to all responses
        addSecurityHeaders(response);

        return response;
    } catch (error) {
        // === Error Handling ===
        // Handle unauthorized errors with proper redirects
        const redirectResponse = await handleUnauthorizedRedirect(error);
        if (redirectResponse) return redirectResponse;

        // Log unhandled errors for monitoring
        tracking.errorWithSource(
            error instanceof Error ? error : new Error('Middleware error'),
            'middleware',
            { path: request.nextUrl.pathname },
        );

        // Return 500 for unhandled errors
        return new NextResponse(null, { status: 500 });
    }
}

/**
 * Middleware matcher configuration
 * Excludes static files and public assets from middleware processing
 */
export const config = {
    matcher: [
        /*
         * Match all request paths except for:
         * - _next/static (static files)
         * - _next/image (image optimization files)
         * - favicon.ico (favicon file)
         * - public folder
         */
        '/((?!_next/static|_next/image|favicon.ico|public/).*)',
    ],
};

// TODO: Consider adding request logging middleware
// TODO: Add performance monitoring
